import React from "react";
import { ArrowUpRight, BellRing, Car, Check, MapPin, Radio, Route, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import SafeImage from "./SafeImage";
import { useLanguage } from "../../contexts/LanguageContext";

const vehicleStatuses = [
  { key: "location", icon: MapPin },
  { key: "safeZones", icon: ShieldCheck },
  { key: "events", icon: BellRing },
];

const signalBars = [34, 54, 43, 72, 58, 86, 64, 78, 48];

export default function CompanyScenesSection() {
  const { t } = useLanguage();
  const copy = t("home.scenes");
  const portfolioCopy = t("home.portfolio");

  return (
    <section id="scenes" className="relative overflow-hidden bg-[#f0fbf6] py-20 sm:py-24">
      <div className="pointer-events-none absolute -right-28 top-20 h-80 w-80 rounded-full bg-brand-200/35 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <div className="max-w-2xl"><p className="home-eyebrow">{copy.eyebrow}</p><h2 className="home-title mt-3">{copy.title}</h2></div>
        <div className="mt-12 grid gap-5 lg:grid-cols-2">
          <article className="company-scene-card company-scene-card--desk overflow-hidden border border-brand-200/80 bg-white shadow-[0_26px_60px_-42px_rgba(8,122,88,.5)]">
            <div className="relative bg-[#eaf9f1] px-5 pt-5 sm:px-7 sm:pt-7"><span className="absolute left-8 top-8 z-10 rounded-full border border-white/70 bg-white/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.14em] text-brand-800 backdrop-blur-sm">{copy.desk}</span><SafeImage src="/images/products/kylendar-mini.webp" alt={t("accessibility.mini")} loading="lazy" decoding="async" className="h-72 w-full object-contain sm:h-80" /></div>
            <div className="p-6 sm:p-7"><p className="text-xs font-bold uppercase tracking-[.16em] text-brand-700">Kylendar</p><h3 className="mt-2 font-display text-2xl font-bold tracking-[-.05em] text-slate-950">{copy.deskTitle}</h3><p className="mt-3 max-w-md text-sm leading-6 text-slate-600">{copy.deskBody}</p><Link to="/kylendar" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-700 transition hover:text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2">{copy.discover} <ArrowUpRight className="h-4 w-4" /></Link></div>
          </article>

          <article className="company-scene-card company-scene-card--road overflow-hidden border border-brand-700 bg-brand-900 text-white shadow-[0_26px_60px_-42px_rgba(8,122,88,.8)]">
            <div className="p-5 sm:p-7">
              <div className="flex items-start justify-between gap-5"><div><p className="text-xs font-bold uppercase tracking-[.16em] text-brand-200">{copy.road}</p><h3 className="mt-2 font-display text-2xl font-bold tracking-[-.05em] text-white">{copy.roadTitle}</h3></div><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-brand-200"><Radio className="h-5 w-5" aria-hidden="true" /></span></div>
              <p className="mt-3 max-w-md text-sm leading-6 text-emerald-50/75">{copy.roadBody}</p>
              <div className="mt-6"><TrevaronStatusConsole /></div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] font-semibold text-brand-50"><span className="flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2.5"><MapPin className="h-3.5 w-3.5 text-brand-200" />{portfolioCopy.location}</span><span className="flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2.5"><Route className="h-3.5 w-3.5 text-brand-200" />{portfolioCopy.trips}</span><span className="flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2.5"><BellRing className="h-3.5 w-3.5 text-brand-200" />{portfolioCopy.events}</span></div>
              <a href="https://trevaron.cloud/" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-200 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:ring-offset-2 focus:ring-offset-brand-900">{copy.visit} <ArrowUpRight className="h-4 w-4" /></a>
            </div>
          </article>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-brand-200/70 pt-5 text-xs font-bold uppercase tracking-[.14em] text-slate-500"><span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-brand-700" /> {copy.quiet}</span><span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-brand-700" /> {copy.clear}</span></div>
      </div>
    </section>
  );
}

function TrevaronStatusConsole() {
  const { t } = useLanguage();
  const copy = t("home.scenes");
  const portfolioCopy = t("home.portfolio");

  return (
    <div className="company-vehicle-console" role="img" aria-label={t("accessibility.trevaronConsole")}>
      <div className="company-vehicle-console-head" aria-hidden="true">
        <span><small>{copy.consoleTitle}</small><strong>{copy.consoleReport}</strong></span>
        <span className="company-vehicle-online">{copy.consolePreview}</span>
      </div>
      <div className="company-vehicle-console-body" aria-hidden="true">
        <div className="company-vehicle-focus">
          <span className="company-vehicle-ring company-vehicle-ring--outer" />
          <span className="company-vehicle-ring company-vehicle-ring--inner" />
          <span className="company-vehicle-core"><Car className="h-7 w-7" /></span>
          <small>{copy.consoleRecent}</small>
        </div>
        <div className="company-vehicle-status-list">
          {vehicleStatuses.map(({ key, icon: Icon }, index) => (
            <div key={key} className="company-vehicle-status-row" style={{ "--status-delay": `${index * 0.45}s` }}>
              <span><Icon className="h-4 w-4" /><small>{portfolioCopy[key]}</small></span><strong>{[portfolioCopy.reported, portfolioCopy.manage, portfolioCopy.review][index]}</strong>
            </div>
          ))}
        </div>
      </div>
      <div className="company-signal-bars" aria-hidden="true">
        <span className="company-signal-bars-label">{copy.consoleHistory}</span>
        <div>{signalBars.map((height, index) => <i key={`${height}-${index}`} style={{ height: `${height}%`, animationDelay: `-${index * 0.28}s` }} />)}</div>
      </div>
    </div>
  );
}
